import { supabase } from '../config/supabase';
import { client } from '../config/mercadopago';

export interface PixPaymentData {
  userId: string;
  userEmail: string;
  userName: string;
  amount: number; // Valor em reais
  description: string;
  tokens?: number; // Quantidade de tokens (opcional)
}

export interface PixPaymentResult {
  preferenceId: string;
  initPoint: string;
  qrCode?: string;
  qrCodeBase64?: string;
}

export interface CheckoutProResult {
  preferenceId: string;
  initPoint: string;
  sandboxInitPoint?: string;
}

class MercadoPagoPixService {
  private accessToken: string;

  constructor() {
    this.accessToken = import.meta.env.VITE_MERCADO_PAGO_ACCESS_TOKEN || '';
    if (!this.accessToken) {
      console.warn('⚠️ VITE_MERCADO_PAGO_ACCESS_TOKEN não configurado');
    }
  }

  /**
   * Buscar pagamentos pendentes do usuário
   */
  async getPendingPayments(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('purchase_intents')
        .select('*')
        .eq('user_uid', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar pagamentos pendentes:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar pagamentos pendentes:', error);
      return [];
    }
  }

  /**
   * Buscar status do pagamento no Mercado Pago
   */
  async getPaymentStatus(paymentId: string): Promise<any> {
    try {
      const response = await fetch(`https://api.mercadopago.com/v1/payments/${paymentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar status do pagamento: ${response.status}`);
      }

      const paymentData = await response.json();
      return {
        status: paymentData.status,
        qr_code: paymentData.point_of_interaction?.transaction_data?.qr_code,
        qr_code_base64: paymentData.point_of_interaction?.transaction_data?.qr_code_base64
      };
    } catch (error) {
      console.error('Erro ao buscar status do pagamento:', error);
      throw error;
    }
  }

  /**
   * Criar preferência de pagamento PIX (baseado no Remaker)
   */
  async createPixPreference(paymentData: PixPaymentData): Promise<PixPaymentResult> {
    try {
      // Validações básicas
      if (!paymentData.userEmail || !paymentData.amount || paymentData.amount <= 0) {
        throw new Error('Dados de pagamento inválidos');
      }

      // Criar preferência usando a mesma estrutura do Remaker
      const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;

      const preferenceData = {
        items: [
          {
            title: paymentData.description || 'Recarga de Tokens',
            quantity: 1,
            currency_id: 'BRL',
            unit_price: parseFloat(paymentData.amount.toFixed(2))
          }
        ],
        payer: {
          email: paymentData.userEmail,
          name: paymentData.userName || 'Usuário'
        },
        metadata: {
          user_email: paymentData.userEmail,
          user_id: paymentData.userId,
          created_at: new Date().toISOString(),
          source: 'overpixel_app'
        },
        back_urls: {
          success: `${baseUrl}/payment/success`,
          failure: `${baseUrl}/payment/failure`,
          pending: `${baseUrl}/payment/pending`
        },
        external_reference: `${paymentData.userId}_${Date.now()}`,
        notification_url: `${import.meta.env.VITE_WEBHOOK_URL || 'https://your-webhook-url.com'}/api/webhooks/mercadopago`,
        expires: true,
        expiration_date_from: new Date().toISOString(),
        expiration_date_to: new Date(Date.now() + 30 * 60 * 1000).toISOString(), // 30 minutos para PIX
        payment_methods: {
          excluded_payment_methods: [],
          excluded_payment_types: [],
          installments: 1 // PIX não permite parcelamento
        }
      };

      // Fazer requisição direta para a API do Mercado Pago
      const response = await fetch('https://api.mercadopago.com/checkout/preferences', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferenceData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erro na API do Mercado Pago:', errorData);
        throw new Error(errorData.message || `Erro HTTP ${response.status}`);
      }

      const preference = await response.json();

      if (!preference.id || !preference.init_point) {
        throw new Error('Resposta inválida da API do Mercado Pago');
      }

      // Salvar intenção de pagamento no Supabase
      await this.savePaymentTransaction({
        preferenceId: preference.id,
        userId: paymentData.userId,
        userEmail: paymentData.userEmail,
        amount: paymentData.amount,
        status: 'pending',
        createdAt: new Date().toISOString(),
        externalReference: `${paymentData.userId}_${Date.now()}`,
        tokens: paymentData.tokens // Passar tokens explicitamente
      });

      return {
        preferenceId: preference.id,
        initPoint: preference.init_point,
        qrCode: preference.qr_code,
        qrCodeBase64: preference.qr_code_base64
      };
    } catch (error: any) {
      console.error('Erro ao criar preferência PIX:', error);
      
      // Tratar erros específicos
      if (error.message?.includes('Invalid credentials') || error.message?.includes('401')) {
        throw new Error('Credenciais do Mercado Pago inválidas. Verifique o ACCESS_TOKEN.');
      }
      
      if (error.message?.includes('Invalid amount') || error.message?.includes('400')) {
        throw new Error('Valor do pagamento inválido.');
      }
      
      if (error.message?.includes('Invalid email')) {
        throw new Error('Email inválido para o pagamento.');
      }
      
      throw new Error(`Falha ao processar pagamento PIX: ${error.message || 'Erro desconhecido'}`);
    }
  }

  /**
   * Criar preferência para Checkout Pro (múltiplas formas de pagamento)
   */
  async createCheckoutProPreference(paymentData: PixPaymentData): Promise<CheckoutProResult> {
    try {
      // Validações básicas
      if (!paymentData.userEmail || !paymentData.amount || paymentData.amount <= 0) {
        throw new Error('Dados de pagamento inválidos');
      }

      // Criar preferência para Checkout Pro
      const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
      
      const preferenceData: any = {
        items: [
          {
            title: paymentData.description || 'Recarga de Tokens',
            quantity: 1,
            currency_id: 'BRL',
            unit_price: parseFloat(paymentData.amount.toFixed(2))
          }
        ],
        payer: {
          email: paymentData.userEmail,
          name: paymentData.userName || 'Usuário'
        },
        metadata: {
          user_email: paymentData.userEmail,
          user_id: paymentData.userId,
          created_at: new Date().toISOString(),
          source: 'overpixel_checkout_pro',
          token_amount: paymentData.tokens // Adicionando tokens aos metadados
        },
        back_urls: {
          success: `${baseUrl}/payment/success`,
          failure: `${baseUrl}/payment/failure`,
          pending: `${baseUrl}/payment/pending`
        },
        external_reference: `${paymentData.userId}_${Date.now()}`,
        expires: true,
        expiration_date_from: new Date().toISOString(),
        expiration_date_to: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 horas para checkout pro
        payment_methods: {
          excluded_payment_methods: [],
          excluded_payment_types: [],
          installments: 12 // Permitir parcelamento até 12x
        }
      };

      // Adicionar notification_url apenas se estiver configurado
      if (import.meta.env.VITE_WEBHOOK_URL) {
        preferenceData.notification_url = `${import.meta.env.VITE_WEBHOOK_URL}/api/webhooks/mercadopago`;
      }

      // Fazer requisição para a API do Mercado Pago
      const response = await fetch('https://api.mercadopago.com/checkout/preferences', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferenceData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erro na API do Mercado Pago:', errorData);
        throw new Error(errorData.message || `Erro HTTP ${response.status}`);
      }

      const preference = await response.json();

      if (!preference.id || !preference.init_point) {
        throw new Error('Resposta inválida da API do Mercado Pago');
      }

      // Salvar intenção de pagamento no Supabase
      await this.savePaymentTransaction({
        preferenceId: preference.id,
        userId: paymentData.userId,
        userEmail: paymentData.userEmail,
        amount: paymentData.amount,
        status: 'pending',
        createdAt: new Date().toISOString(),
        externalReference: `${paymentData.userId}_${Date.now()}`
      });

      return {
        preferenceId: preference.id,
        initPoint: preference.init_point,
        sandboxInitPoint: preference.sandbox_init_point
      };
    } catch (error: any) {
      console.error('Erro ao criar preferência Checkout Pro:', error);
      
      // Tratar erros específicos
      if (error.message?.includes('Invalid credentials') || error.message?.includes('401')) {
        throw new Error('Credenciais do Mercado Pago inválidas. Verifique o ACCESS_TOKEN.');
      }
      
      if (error.message?.includes('Invalid email')) {
        throw new Error('Email inválido para o pagamento.');
      }
      
      throw new Error(error.message || 'Falha ao processar Checkout Pro');
    }
  }

  /**
   * Verificar status do pagamento
   */
  async checkPaymentStatus(preferenceId: string): Promise<any> {
    try {
      // Buscar pagamentos por preference_id
      const response = await fetch(`https://api.mercadopago.com/v1/payments/search?preference_id=${preferenceId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP ${response.status}`);
      }

      const searchResult = await response.json();
      const results = searchResult.results || [];

      // Procurar por pagamento aprovado
      for (const payment of results) {
        if (payment.status === 'approved') {
          return {
            success: true,
            approved: true,
            paymentId: payment.id,
            amount: payment.transaction_amount,
            status: payment.status,
            paymentMethod: payment.payment_method_id
          };
        }
      }

      return {
        success: true,
        approved: false,
        status: results[0]?.status || 'pending'
      };
    } catch (error: any) {
      console.error('Erro ao verificar status do pagamento:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Buscar preferência existente do Mercado Pago
   */
  async getExistingPreference(preferenceId: string): Promise<PixPaymentResult> {
    try {
      const response = await fetch(`https://api.mercadopago.com/checkout/preferences/${preferenceId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar preferência: ${response.status}`);
      }

      const preference = await response.json();

      return {
        preferenceId: preference.id,
        initPoint: preference.init_point,
        qrCode: preference.qr_code,
        qrCodeBase64: preference.qr_code_base64
      };
    } catch (error: any) {
      console.error('Erro ao buscar preferência existente:', error);
      throw new Error(`Falha ao buscar preferência: ${error.message || 'Erro desconhecido'}`);
    }
  }

  /**
   * Salvar intenção de compra no purchase_intents
   */
  private async savePaymentTransaction(data: {
    preferenceId: string;
    userId: string;
    userEmail: string;
    amount: number;
    status: string;
    createdAt: string;
    externalReference: string;
    tokens?: number;
  }): Promise<void> {
    try {
      // Calcular tokens baseado no valor pago se não for fornecido
      const tokens = data.tokens || this.calculateTokens(data.amount);
      
      // Inserir na purchase_intents
      const { error } = await supabase
        .from('purchase_intents')
        .insert({
          user_uid: data.userId,
          purchase_id: data.preferenceId,
          amount: data.amount,
          tokens_to_credit: tokens,
          payment_method: 'pix',
          status: data.status,
          created_at: data.createdAt,
          metadata: {
            source: 'overpixel_pix_service',
            external_reference: data.externalReference,
            user_email: data.userEmail
          }
        });

      if (error) {
        console.error('Erro ao salvar intenção de compra:', error);
      } else {
        console.log(`✅ Intenção de compra salva: ${tokens} tokens (R$ ${data.amount})`);
      }
    } catch (error) {
      console.error('Erro ao salvar transação de pagamento:', error);
    }
  }

  /**
   * Calcular tokens baseado no valor (R$1 = 10 tokens)
   */
  calculateTokens(amountBRL: number): number {
    return Math.round(amountBRL * 10);
  }
}

export const mercadopagoPixService = new MercadoPagoPixService();
export default mercadopagoPixService;